import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { switchMap, map, catchError, mergeMap } from 'rxjs/operators';

import * as PlaylistActions from '../actions/playlist.actions';
import { PlaylistService } from '../services/playlist.service';
import { FirebaseStorageError } from '../errors/firebase-storage-error';
import { GeneralError } from '../errors/general-error';
import { dispatchAppError } from '../actions/core.actions';
import { FirestoreError } from '../errors/firestore-error';

@Injectable()
export class PlaylistEffects {
  private LOG_SOURCE = this.constructor.name;

  findPlaylists$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlaylistActions.findPlaylists),
      switchMap(() =>
        this.playlistService.findPlaylists().pipe(
          map(playlists => PlaylistActions.findPlaylistsSuccess({ playlists })),
          catchError(e =>
            of(PlaylistActions.findPlaylistsFailure(), dispatchAppError({ source: this.LOG_SOURCE, error: FirestoreError.from(e) }))
          )
        )
      )
    )
  );

  findPlaylistProblemIds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlaylistActions.findPlaylistProblemIds),
      switchMap(action =>
        this.playlistService.findPlyaListProblemIds(action.subjectId, action.playlistId).pipe(
          map(playlistProblemIds => PlaylistActions.findPlaylistProblemIdsSuccess({ playlistProblemIds })),
          catchError(e =>
            of(
              PlaylistActions.findPlaylistProblemIdsFailure(),
              dispatchAppError({ source: this.LOG_SOURCE, error: FirestoreError.from(e) })
            )
          )
        )
      )
    )
  );

  findPlaylistProblems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlaylistActions.findPlaylistProblems),
      switchMap(action =>
        this.playlistService.findPlaylistProblems(action.subjectId, action.playlistId).pipe(
          map(playlistProblems => PlaylistActions.findPlaylistProblemsSuccess({ subjectId: action.subjectId, playlistProblems })),
          catchError(e =>
            of(PlaylistActions.findPlaylistProblemsFailure(), dispatchAppError({ source: this.LOG_SOURCE, error: FirestoreError.from(e) }))
          )
        )
      )
    )
  );

  resolvePdfObjectUrl$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlaylistActions.resolvePdfObjectUrl),
      mergeMap(action =>
        this.playlistService.getPdfObjectUrl(action.pdfPath).pipe(
          map(objectUrl => PlaylistActions.resolvePdfObjectUrlSuccess({ pdfPath: action.pdfPath, objectUrl })),
          catchError(e => {
            const error = e instanceof FirebaseStorageError ? e : GeneralError.unknown(e);
            return of(
              PlaylistActions.resolvePdfObjectUrlFailure({ pdfPath: action.pdfPath }),
              dispatchAppError({ source: this.LOG_SOURCE, error })
            );
          })
        )
      )
    )
  );

  constructor(private actions$: Actions, private playlistService: PlaylistService) {}
}
